// FMI Weather Data Processor
// Converts raw FMI XML data to structured format for visualization

export class WeatherDataProcessor {
  constructor() {
    this.parameterMap = {
      't2m': 'temperature',
      'rh': 'humidity', 
      'r_1h': 'precipitation',
      'ws_10min': 'windSpeed',
      'wd_10min': 'windDirection',
      'p_sea': 'pressure'
    };
  }

  // Process historical data from FMI cache file
  processHistoricalData(fmiData) {
    try {
      if (!fmiData || !fmiData.chunks) {
        throw new Error('Invalid FMI data structure');
      }

      const allObservations = [];

      // Extract observations from all chunks
      fmiData.chunks.forEach(chunk => {
        if (chunk.data && chunk.data['wfs:FeatureCollection']) {
          const features = this.extractFeatures(chunk.data['wfs:FeatureCollection']);
          allObservations.push(...features);
        }
      });

      // Group and aggregate data by time periods
      const processedData = {
        raw: allObservations,
        yearly: this.aggregateByYear(allObservations),
        monthly: this.aggregateByMonth(allObservations),
        daily: this.aggregateByDay(allObservations),
        metadata: {
          station: fmiData.station,
          period: fmiData.period,
          totalObservations: allObservations.length,
          processedAt: new Date().toISOString()
        }
      };

      return processedData;

    } catch (error) {
      console.error('Error processing FMI data:', error);
      throw error;
    }
  }

  // Extract observation features from FMI XML structure
  extractFeatures(featureCollection) {
    const observations = [];
    
    try {
      const members = Array.isArray(featureCollection['wfs:member']) 
        ? featureCollection['wfs:member']
        : [featureCollection['wfs:member']];

      members.forEach(member => {
        if (member && member['omso:GridSeriesObservation']) {
          const observation = this.parseObservation(member['omso:GridSeriesObservation']);
          if (observation) {
            observations.push(observation);
          }
        }
      });

    } catch (error) {
      console.warn('Error extracting features:', error);
    }

    return observations;
  }

  // Parse individual observation from FMI structure
  parseObservation(gridObservation) {
    try {
      // Extract time period
      const timePeriod = gridObservation['om:phenomenonTime']['gml:TimePeriod'];
      const startTime = timePeriod['gml:beginPosition'];
      const endTime = timePeriod['gml:endPosition'];

      // Extract measurement data
      const result = gridObservation['om:result'];
      const measurements = this.parseMeasurements(result);

      return {
        startTime: new Date(startTime),
        endTime: new Date(endTime),
        measurements,
        station: this.extractStationInfo(gridObservation)
      };

    } catch (error) {
      console.warn('Error parsing observation:', error);
      return null;
    }
  }

  // Parse measurement values from result structure
  parseMeasurements(result) {
    const measurements = {};
    
    try {
      // FMI data structure can vary, handle different formats
      if (result && result['gmlcov:MultiPointCoverage']) {
        const coverage = result['gmlcov:MultiPointCoverage'];
        
        // Extract parameters and values
        const rangeType = coverage['gmlcov:rangeType'];
        const rangeSet = coverage['gml:rangeSet'];
        
        if (rangeType && rangeSet) {
          // Map parameter names to values
          // This is a simplified version - real implementation would handle
          // the full GML coverage structure
          measurements.temperature = this.extractParameterValue(rangeSet, 't2m');
          measurements.humidity = this.extractParameterValue(rangeSet, 'rh');
          measurements.precipitation = this.extractParameterValue(rangeSet, 'r_1h');
          measurements.windSpeed = this.extractParameterValue(rangeSet, 'ws_10min');
        }
      }

    } catch (error) {
      console.warn('Error parsing measurements:', error);
    }

    return measurements;
  }

  // Extract parameter value from range set (simplified)
  extractParameterValue(rangeSet, parameter) {
    try {
      // This is a placeholder - real implementation would parse the
      // complex GML data structure with proper coordinate mapping
      return Math.random() * 30 - 10; // Mock data for now
    } catch {
      return null;
    }
  }

  // Extract station information
  extractStationInfo(observation) {
    try {
      // Extract station metadata from observation
      return {
        id: 'helsinki_kaisaniemi',
        name: 'Helsinki Kaisaniemi',
        coordinates: { lat: 60.175, lon: 24.949 }
      };
    } catch {
      return null;
    }
  }

  // Aggregate observations by year
  aggregateByYear(observations) {
    const yearlyData = {};

    observations.forEach(obs => {
      const year = obs.startTime.getFullYear().toString();
      
      if (!yearlyData[year]) {
        yearlyData[year] = {
          observations: [],
          temperature: { values: [], avg: 0, min: Infinity, max: -Infinity },
          humidity: { values: [], avg: 0, min: Infinity, max: -Infinity },
          precipitation: { values: [], total: 0, days: 0 },
          wind: { values: [], avg: 0, max: -Infinity }
        };
      }

      yearlyData[year].observations.push(obs);
      this.updateAggregates(yearlyData[year], obs.measurements);
    });

    // Calculate final statistics
    Object.values(yearlyData).forEach(yearData => {
      this.finalizeAggregates(yearData);
    });

    return yearlyData;
  }

  // Aggregate observations by month
  aggregateByMonth(observations) {
    const monthlyData = {};

    observations.forEach(obs => {
      const year = obs.startTime.getFullYear();
      const month = (obs.startTime.getMonth() + 1).toString().padStart(2, '0');
      const key = `${year}-${month}`;
      
      if (!monthlyData[key]) {
        monthlyData[key] = {
          observations: [],
          temperature: { values: [], avg: 0, min: Infinity, max: -Infinity },
          humidity: { values: [], avg: 0, min: Infinity, max: -Infinity },
          precipitation: { values: [], total: 0, days: 0 },
          wind: { values: [], avg: 0, max: -Infinity }
        };
      }

      monthlyData[key].observations.push(obs);
      this.updateAggregates(monthlyData[key], obs.measurements);
    });

    // Calculate final statistics
    Object.values(monthlyData).forEach(monthData => {
      this.finalizeAggregates(monthData);
    });

    return monthlyData;
  }

  // Aggregate observations by day
  aggregateByDay(observations) {
    const dailyData = {};

    observations.forEach(obs => {
      const year = obs.startTime.getFullYear();
      const month = (obs.startTime.getMonth() + 1).toString().padStart(2, '0');
      const day = obs.startTime.getDate().toString().padStart(2, '0');
      const key = `${year}-${month}-${day}`;
      
      if (!dailyData[key]) {
        dailyData[key] = {
          observations: [],
          temperature: { values: [], avg: 0, min: Infinity, max: -Infinity },
          humidity: { values: [], avg: 0, min: Infinity, max: -Infinity },
          precipitation: { values: [], total: 0, hours: 0 },
          wind: { values: [], avg: 0, max: -Infinity }
        };
      }

      dailyData[key].observations.push(obs);
      this.updateAggregates(dailyData[key], obs.measurements);
    });

    // Calculate final statistics
    Object.values(dailyData).forEach(dayData => {
      this.finalizeAggregates(dayData);
    });

    return dailyData;
  }

  // Update running aggregates with new measurement
  updateAggregates(aggregateData, measurements) {
    if (measurements.temperature !== null && measurements.temperature !== undefined) {
      aggregateData.temperature.values.push(measurements.temperature);
      aggregateData.temperature.min = Math.min(aggregateData.temperature.min, measurements.temperature);
      aggregateData.temperature.max = Math.max(aggregateData.temperature.max, measurements.temperature);
    }

    if (measurements.humidity !== null && measurements.humidity !== undefined) {
      aggregateData.humidity.values.push(measurements.humidity);
      aggregateData.humidity.min = Math.min(aggregateData.humidity.min, measurements.humidity);
      aggregateData.humidity.max = Math.max(aggregateData.humidity.max, measurements.humidity);
    }

    if (measurements.precipitation !== null && measurements.precipitation !== undefined) {
      aggregateData.precipitation.values.push(measurements.precipitation);
      aggregateData.precipitation.total += measurements.precipitation;
      if (measurements.precipitation > 0) {
        aggregateData.precipitation.days += 1;
      }
    }

    if (measurements.windSpeed !== null && measurements.windSpeed !== undefined) {
      aggregateData.wind.values.push(measurements.windSpeed);
      aggregateData.wind.max = Math.max(aggregateData.wind.max, measurements.windSpeed);
    }
  }

  // Finalize aggregate calculations
  finalizeAggregates(aggregateData) {
    // Calculate averages
    if (aggregateData.temperature.values.length > 0) {
      aggregateData.temperature.avg = 
        aggregateData.temperature.values.reduce((a, b) => a + b, 0) / 
        aggregateData.temperature.values.length;
    }

    if (aggregateData.humidity.values.length > 0) {
      aggregateData.humidity.avg = Math.round(
        aggregateData.humidity.values.reduce((a, b) => a + b, 0) / 
        aggregateData.humidity.values.length
      );
    }

    if (aggregateData.wind.values.length > 0) {
      aggregateData.wind.avg = 
        aggregateData.wind.values.reduce((a, b) => a + b, 0) / 
        aggregateData.wind.values.length;
    }

    // Handle infinite values
    if (aggregateData.temperature.min === Infinity) aggregateData.temperature.min = 0;
    if (aggregateData.temperature.max === -Infinity) aggregateData.temperature.max = 0;
    if (aggregateData.humidity.min === Infinity) aggregateData.humidity.min = 0;
    if (aggregateData.humidity.max === -Infinity) aggregateData.humidity.max = 0;
    
    // Round precipitation total to 2 decimal places
    aggregateData.precipitation.total = Math.round(aggregateData.precipitation.total * 100) / 100;
    if (aggregateData.wind.max === -Infinity) aggregateData.wind.max = 0;
  }

  // Generate mock data for testing (until real FMI parsing is complete)
  generateMockData(year = '2023') {
    const mockData = {
      yearly: {},
      monthly: {},
      daily: {}
    };

    // Generate yearly data
    mockData.yearly[year] = {
      temperature: { 
        avg: 8.2 + (Math.random() - 0.5) * 4, 
        min: -15.3 + (Math.random() - 0.5) * 5, 
        max: 28.7 + (Math.random() - 0.5) * 5 
      },
      humidity: { 
        avg: 78 + (Math.random() - 0.5) * 10, 
        min: 34 + (Math.random() - 0.5) * 10, 
        max: 98 
      },
      precipitation: { 
        total: 642 + (Math.random() - 0.5) * 200, 
        days: 156 + Math.floor((Math.random() - 0.5) * 30) 
      },
      wind: { 
        avg: 4.2 + (Math.random() - 0.5) * 2, 
        max: 18.3 + (Math.random() - 0.5) * 5 
      }
    };

    // Generate monthly data for the year
    for (let month = 1; month <= 12; month++) {
      const monthKey = `${year}-${month.toString().padStart(2, '0')}`;
      const seasonFactor = Math.sin((month - 1) * Math.PI / 6); // Seasonal variation
      
      mockData.monthly[monthKey] = {
        temperature: { 
          avg: 8 + seasonFactor * 15 + (Math.random() - 0.5) * 3,
          min: 5 + seasonFactor * 12 + (Math.random() - 0.5) * 5,
          max: 12 + seasonFactor * 18 + (Math.random() - 0.5) * 4
        },
        humidity: { 
          avg: 75 + (Math.random() - 0.5) * 15, 
          min: 45 + (Math.random() - 0.5) * 15, 
          max: 95 + (Math.random() - 0.5) * 5 
        },
        precipitation: { 
          total: 50 + (Math.random() - 0.5) * 40, 
          days: 12 + Math.floor((Math.random() - 0.5) * 8) 
        },
        wind: { 
          avg: 4 + (Math.random() - 0.5) * 2, 
          max: 15 + (Math.random() - 0.5) * 8 
        }
      };
    }

    // Generate some daily data for current month
    const currentMonth = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear().toString();
    
    for (let day = 1; day <= 10; day++) {
      const dayKey = `${currentYear}-${currentMonth.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
      
      mockData.daily[dayKey] = {
        temperature: { 
          avg: 15 + (Math.random() - 0.5) * 10,
          min: 10 + (Math.random() - 0.5) * 8,
          max: 20 + (Math.random() - 0.5) * 8
        },
        humidity: { 
          avg: 70 + (Math.random() - 0.5) * 20, 
          min: 50 + (Math.random() - 0.5) * 15, 
          max: 90 + (Math.random() - 0.5) * 10 
        },
        precipitation: { 
          total: Math.round(Math.random() * 10 * 100) / 100, 
          hours: Math.floor(Math.random() * 6) 
        },
        wind: { 
          avg: 3 + Math.random() * 4, 
          max: 6 + Math.random() * 8 
        }
      };
    }

    return mockData;
  }
}

export default WeatherDataProcessor;